<ng-container *transloco="let t; read: 'event-create-dialog'">
  <p-toast position="bottom-center"></p-toast>

  <ng-template #eventCreateDialogEditor>
    <form class="event-create-dialog__form" [formGroup]="form">
      <!-- Date & Time Section -->
      <h3 class="event-create-dialog__section-title">{{ t('dateTimeSection') }}</h3>

      <!-- Start Time -->
      <label class="event-create-dialog__form-field">
        <span class="event-create-dialog__form-field-label">{{ t('start') }}</span>
        <input class="w-full" formControlName="startTime" pInputText type="datetime-local" [step]="900" />
        <app-form-field-help [control]="startTime">
          <ng-container form-field-help-errors>
            <div *ngIf="startTime?.errors?.required">{{ t('startTimeRequired') }}</div>
            <div *ngIf="startTime?.errors?.stepSizeSeconds">{{ t('startTimeStepError') }}</div>
          </ng-container>
        </app-form-field-help>
      </label>

      <!-- End Time -->
      <label class="event-create-dialog__form-field">
        <span class="event-create-dialog__form-field-label">{{ t('end') }}</span>
        <input
          class="w-full"
          formControlName="endTime"
          pInputText
          type="datetime-local"
          [ngClass]="{ 'ng-dirty ng-invalid': form.dirty && form.errors?.isBeforeControl }"
          [step]="900"
        />
        <app-form-field-help [control]="form">
          <ng-container form-field-help-errors>
            <div *ngIf="endTime?.errors?.required">{{ t('endTimeRequired') }}</div>
            <div *ngIf="endTime?.errors?.stepSizeSeconds">{{ t('endTimeStepError') }}</div>
            <div *ngIf="form.errors?.isBeforeControl">{{ t('endTimeBeforeStartTimeError') }}</div>
          </ng-container>
        </app-form-field-help>
      </label>

      <!-- Overlap Warning -->
      <div *ngIf="hasOverlap" class="event-create-dialog__overlap-warning">
        <mat-icon>warning</mat-icon>
        <span>{{ getOverlapErrorMessage() }}</span>
      </div>

      <!-- Repeat Section -->
      <ng-container *ngIf="shouldShowRepeatSection()">
        <div class="event-create-dialog__repeat-header">
          <p-checkbox formControlName="isRepeating" inputId="isRepeating" [binary]="true"></p-checkbox>
          <h3 class="event-create-dialog__section-title event-create-dialog__section-title--inline">
            <label for="isRepeating">{{ t('repeatSection') }}</label>
          </h3>
        </div>

      <!-- RRule Editor -->
      <ng-container *ngIf="shouldShowRepeatEndSection()">
        <app-rrule-editor formControlName="rrule" [disableStartEditing]="true"></app-rrule-editor>

        <h3 class="event-create-dialog__section-title">{{ t('repeatEndSection') }}</h3>

        <div class="event-create-dialog__repeat-end-radio-row">
          <p-radioButton
            formControlName="repeatEnd"
            inputId="continueIndefinitely"
            name="repeatEnd"
            value="CONTINUE_INDEFINITELY"
            [ariaLabel]="t('continueIndefinitely')"
            [label]="t('continueIndefinitely')"
          ></p-radioButton>

          <p-radioButton
            formControlName="repeatEnd"
            inputId="endDate"
            name="repeatEnd"
            value="END_DATE"
            [ariaLabel]="t('endOnSpecificDate')"
            [label]="t('endOnSpecificDate')"
          ></p-radioButton>
        </div>

        <app-form-field-help [control]="repeatEnd">
          <ng-container form-field-help-errors>
            <div *ngIf="repeatEnd?.errors?.required">{{ t('repeatEndRequired') }}</div>
          </ng-container>
        </app-form-field-help>

        <label *ngIf="shouldShowExtendScheduleNumDays()" class="event-create-dialog__form-field">
          <span class="event-create-dialog__form-field-label">{{ t('extendScheduleNumDaysLabel') }}</span>
          <input
            class="w-full"
            formControlName="extendScheduleNumDays"
            pInputText
            type="number"
            [attr.aria-label]="t('extendScheduleNumDaysLabel')"
            [max]="365"
            [min]="1"
          />
          <app-form-field-help [control]="extendScheduleNumDays">
            <ng-container form-field-help-errors>
              <div *ngIf="extendScheduleNumDays?.errors?.required">{{ t('extendScheduleNumDaysRequired') }}</div>
              <div *ngIf="extendScheduleNumDays?.errors?.min || extendScheduleNumDays?.errors?.max">
                {{ t('extendScheduleNumDaysRange') }}
              </div>
            </ng-container>
          </app-form-field-help>
          <p class="event-create-dialog__help-text">
            {{ t('extendScheduleNumDaysHelp', { days: extendScheduleNumDays?.value || 90 }) }}
          </p>
        </label>

        <label *ngIf="shouldShowRepeatEndDatePicker()" class="event-create-dialog__form-field">
          <input
            class="w-full"
            formControlName="repeatEndDate"
            pInputText
            type="datetime-local"
            [attr.aria-label]="t('repeatEndDate')"
            [ngClass]="{ 'ng-dirty ng-invalid': form.dirty && form.errors?.repeatEndDateMustBeAfterEndTime }"
            [step]="900"
          />
          <app-form-field-help [control]="form">
            <ng-container form-field-help-errors>
              <div *ngIf="repeatEndDate?.errors?.required">{{ t('repeatEndDateRequired') }}</div>
              <div *ngIf="repeatEndDate?.errors?.stepSizeSeconds">{{ t('repeatEndDateStepError') }}</div>
              <div *ngIf="form.errors?.repeatEndDateMustBeAfterEndTime">{{ t('repeatEndDateMustBeAfterEndTime') }}</div>
            </ng-container>
          </app-form-field-help>
        </label>
      </ng-container>
      </ng-container>

      <!-- Assignments Section -->
      <h3 class="event-create-dialog__section-title">{{ t('assignmentsSection') }}</h3>

      <!-- Primary and Backup User Assignments -->
      <div class="event-create-dialog__assignments-row">
        <div class="event-create-dialog__assignment-field">
          <label class="event-create-dialog__form-field-label" for="assignedUserId">{{ t('primaryUser') }}</label>
          <p-dropdown
            appendTo="body"
            filterBy="label"
            formControlName="assignedUserId"
            inputId="assignedUserId"
            [ariaLabel]="t('primaryUser')"
            [autoDisplayFirst]="true"
            [filter]="true"
            [options]="userSelectOptions"
            [placeholder]="t('selectUser')"
            [showClear]="false"
            [style]="{ width: '100%' }"
          />
        </div>

        <div class="event-create-dialog__assignment-field">
          <label class="event-create-dialog__form-field-label" for="assignedBackupUserId">{{ t('backupUser') }}</label>
          <p-dropdown
            appendTo="body"
            filterBy="label"
            formControlName="assignedBackupUserId"
            inputId="assignedBackupUserId"
            [ariaLabel]="t('backupUser')"
            [autoDisplayFirst]="true"
            [filter]="true"
            [options]="userSelectOptions"
            [showClear]="false"
            [style]="{ width: '100%' }"
          />
        </div>
      </div>
    </form>
  </ng-template>
  <app-settings
    [doUpload]="upload"
    [editorTemplate]="eventCreateDialogEditor"
    [isValid]="isValid"
    [refreshData]="refreshData"
    [resetForm]="resetForm"
  >
  </app-settings>
</ng-container>
